import { useEffect, useId } from 'react';
import { useNavigationStore } from '@/stores/navigationStore';

/**
 * Hook to register a component's dirty state with the global navigation system.
 * Automatically handles:
 * - In-app navigation confirmation (via custom modal when using useStanRouter)
 * - Browser warnings for page refresh and tab close (native browser alert)
 * - Cleanup on unmount
 *
 * Note: Browser back/forward navigation cannot be reliably intercepted with BrowserRouter.
 * Users will lose unsaved changes if they use browser back/forward buttons.
 *
 * @param isDirty - Whether the component has unsaved changes
 * @param componentId - Optional unique identifier for the component. If not provided, uses React's useId()
 *
 * @example
 * ```tsx
 * const MyForm = () => {
 *   const { formState: { isDirty } } = useForm();
 *
 *   // Automatically registers/unregisters this component and handles navigation warnings
 *   useDirtyState(isDirty);
 *
 *   return <form>...</form>;
 * };
 * ```
 *
 * @example
 * ```tsx
 * // With custom component ID
 * const EmailEditor = () => {
 *   const { emailId } = useParams();
 *   const { formState: { isDirty } } = useForm();
 *
 *   useDirtyState(isDirty, `email-editor-${emailId}`);
 *
 *   return <form>...</form>;
 * };
 * ```
 */
export function useDirtyState(isDirty: boolean, componentId?: string) {
    const autoId = useId();
    const id = componentId || autoId;
    const { registerDirty, unregisterDirty, isDirty: isAnyDirty } = useNavigationStore();

    // Register/unregister based on dirty state
    useEffect(() => {
        if (isDirty) {
            registerDirty(id);
        } else {
            unregisterDirty(id);
        }
    }, [isDirty, id, registerDirty, unregisterDirty]);

    // Cleanup on unmount - for preventing stale dirty state
    useEffect(() => {
        return () => {
            unregisterDirty(id);
        };
    }, [id, unregisterDirty]);

    // Handle browser navigation (refresh/close)
    // Note: Browser back/forward cannot be reliably blocked with BrowserRouter
    // In-app navigation is handled by useStanRouter with the confirmation modal
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            // Only show warning if there are unsaved changes
            if (isAnyDirty()) {
                // Modern browsers require returnValue to be set
                event.preventDefault();
                event.returnValue = '';

                // Some browsers use the return value
                return '';
            }

            return undefined;
        };

        // Add event listener for page refresh and tab close
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Cleanup on unmount
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isAnyDirty]);
}
